import {
  S3Client,
  GetObjectTaggingCommand,
  PutObjectTaggingCommand,
  GetObjectCommand,
  PutObjectCommand,
  ListObjectsV2Command,
  ListObjectsCommandOutput,
  ListObjectVersionsCommand,
  ListObjectVersionsCommandOutput,
  CommonPrefix,
  DeleteMarkerEntry,
} from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { IMsalContext } from "@azure/msal-react";
import { fetchAwsCredentials } from "components/Upload/uploadApi";
import axios from "axios";
import { getAccessToken, getS3grantAccessToken } from "config/token";
import { NEXUS_API_BASE_URL } from "const";
import { IGetAgCreds } from ".";
import { Credentials } from "components/Users/components/AccessData/types";
import { generateKeysApi, loadAwsSession, storeAwsSession } from "utils/awsSession";
import { getObjectVersionsPage } from "components/InlineVersionsTable/inlineVersionsApi";

interface DomainData {
  display_name: string;
  bucket_name: Record<string, string[]>;
}

export interface IGetAgCredsResponse {
  aws_access_key_id: string;
  aws_secret_access_key: string;
  aws_session_token: string;
  access_level: "READ" | "READWRITE";
  user_role: "owner" | "rw" | "ro";
}

export interface ObjectProtectionStatus {
  guardDutyStatus?: string;
  validationStatus?: string;
  isQuarantined: boolean;
  hasVulnerability: boolean;
  /** Set when the status could not be resolved; callers must fail closed on it. */
  isStatusUnknown: boolean;
}

const UNKNOWN_PROTECTION_STATUS: ObjectProtectionStatus = {
  isQuarantined: false,
  hasVulnerability: false,
  isStatusUnknown: true,
};

/** Single source of truth for "this object must not be selected, previewed or downloaded". */
export function isRestrictedProtectionStatus(
  status?: Partial<ObjectProtectionStatus> | null,
): boolean {
  return !!(status?.isQuarantined || status?.hasVulnerability || status?.isStatusUnknown);
}


/**
 * Check if an S3 object is registered (has the gluetable=True tag)
 */
export async function checkIsRegistered(
  bucket: string,
  key: string,
  msalInstance: IMsalContext,
  selecteSpaceRegion: string,
): Promise<boolean> {
  try {
    const creds = await fetchAwsCredentials(msalInstance, bucket);
    if (!creds) throw new Error("No AWS credentials available");

    const s3 = new S3Client({
      region: selecteSpaceRegion || creds.region,
      credentials: {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
      },
    });

    const command = new GetObjectTaggingCommand({ Bucket: bucket, Key: key });
    const data = await s3.send(command);
    const tags = data.TagSet || [];
    const tagMap: Record<string, string> = {};
    tags.forEach(tag => {
      if (tag.Key && tag.Value) tagMap[tag.Key] = tag.Value;
    });
    return tagMap.gluetable === "True";
  } catch {
    return false;
  }
}

/**
 * Fetch GuardDuty and validation tags for the files shown in the explorer.
 *
 * S3 Access Grants credentials never include s3:GetObjectTagging, so tagging
 * is resolved through the Nexus API with a data-account role.
 * Individual failures are treated as an unknown status so one inaccessible
 * object does not prevent the rest of the folder from loading.
 */
export async function getObjectProtectionStatuses(
  bucket: string,
  keys: string[],
  msalInstance: IMsalContext,
  region: string,
  spaceName: string,
): Promise<Record<string, ObjectProtectionStatus>> {
  if (!keys.length) return {};

  try {
    const token = await getAccessToken(msalInstance);
    const bearerToken = `Bearer ${token?.token ?? ""}`;
    const requestURL = `${NEXUS_API_BASE_URL}/nexus/object-protection-statuses`;

    const response = await axios.post(
      requestURL,
      {
        space_name: spaceName,
        bucket_name: bucket,
        region,
        object_keys: keys,
      },
      {
        headers: {
          Authorization: bearerToken,
          "Content-Type": "application/json",
        },
      },
    );

    const statuses = response.data?.statuses ?? {};
    return Object.fromEntries(
      keys.map(key => {
        const status = statuses[key];
        if (!status) {
          return [key, { ...UNKNOWN_PROTECTION_STATUS }];
        }
        return [
          key,
          {
            guardDutyStatus: status.guardDutyStatus,
            validationStatus: status.validationStatus,
            isQuarantined: !!status.isQuarantined,
            hasVulnerability: !!status.hasVulnerability,
            isStatusUnknown: false,
          },
        ];
      }),
    );
  } catch (error) {
    console.error("Failed to fetch object protection statuses:", error);
    return Object.fromEntries(keys.map(key => [key, { ...UNKNOWN_PROTECTION_STATUS }]));
  }
}

/**
 * Register asset by setting the gluetable tag to True
 */
export async function registerAssetInDatazone(
  bucket: string,
  key: string,
  msalInstance: IMsalContext,
  selecteSpaceRegion: string,
): Promise<{ success: boolean; message: string }> {
  try {
    const creds = await fetchAwsCredentials(msalInstance, bucket);
    if (!creds) throw new Error("No AWS credentials available");

    const s3 = new S3Client({
      region: selecteSpaceRegion || creds.region,
      credentials: {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
      },
    });

    // 1. Fetch existing tags
    const getTags = new GetObjectTaggingCommand({ Bucket: bucket, Key: key });
    const getData = await s3.send(getTags);
    const tagMap: Record<string, string> = {};
    (getData.TagSet || []).forEach(tag => {
      if (tag.Key && tag.Value) tagMap[tag.Key] = tag.Value;
    });

    if (tagMap.gluetable === "True") {
      return { success: false, message: "File is already registered as an asset" };
    }

    // 2. Update tags
    tagMap.gluetable = "True";
    const putTags = new PutObjectTaggingCommand({
      Bucket: bucket,
      Key: key,
      Tagging: {
        TagSet: Object.entries(tagMap).map(([Key, Value]) => ({ Key, Value })),
      },
    });
    await s3.send(putTags);

    return { success: true, message: "Asset is being registered. This may take up to 5 minutes" };
  } catch (err: any) {
    return { success: false, message: err?.message || "Failed to register asset" };
  }
}

/**
 * Returns a time-limited S3 download URL for the specified object.
 */
export async function getS3DownloadUrl(
  bucket: string,
  key: string,
  msalInstance: IMsalContext,
  selecteSpaceRegion: string,
  agCredsResponseData: IGetAgCredsResponse | boolean,
): Promise<string | null> {
  try {
    let credentials;
    let region = selecteSpaceRegion;

    // Priority 1: Use agCredsResponseData if available
    if (typeof agCredsResponseData === "object" && "aws_access_key_id" in agCredsResponseData) {
      credentials = {
        accessKeyId: agCredsResponseData.aws_access_key_id,
        secretAccessKey: agCredsResponseData.aws_secret_access_key,
        sessionToken: agCredsResponseData.aws_session_token,
      };
    } else {
      // Priority 2: Fallback to localStorage session
      const creds = await fetchAwsCredentials(msalInstance, bucket);
      if (!creds) throw new Error("No AWS credentials available");

      credentials = {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
      };
      region = region || creds.region;
    }

    const s3 = new S3Client({
      region,
      credentials,
    });

    const command = new GetObjectCommand({
      Bucket: bucket,
      Key: key,
      ResponseContentDisposition: `attachment; filename="${key.split("/").pop()}"`,
    });

    return await getSignedUrl(s3, command, { expiresIn: 300 });
  } catch {
    return null;
  }
}

/**
 * Create a folder in S3 by creating an empty object with a trailing slash in the key.
 */
export async function createS3Folder(
  bucket: string,
  region: any,
  prefix: string,
  msalInstance: IMsalContext,
  agCredsResponseData: IGetAgCredsResponse | boolean,
): Promise<void> {
  const creds = await fetchAwsCredentials(msalInstance, bucket);
  if (!creds) throw new Error("No AWS credentials available");

  const s3 = new S3Client({
    region: region,
    credentials: {
      accessKeyId: typeof agCredsResponseData === "object" && "aws_access_key_id" in agCredsResponseData
        ? agCredsResponseData.aws_access_key_id
        : creds.accessKeyId,
      secretAccessKey: typeof agCredsResponseData === "object" && "aws_secret_access_key" in agCredsResponseData
        ? agCredsResponseData.aws_secret_access_key
        : creds.secretAccessKey,
      sessionToken: typeof agCredsResponseData === "object" && "aws_session_token" in agCredsResponseData
        ? agCredsResponseData.aws_session_token
        : creds.sessionToken,
    },
  });

  const command = new PutObjectCommand({
    Bucket: bucket,
    Key: prefix,
    Body: "",
  });

  await s3.send(command);
}



// Returns all files (not folders) under the given prefix (recursive)
export async function getAllFilesInFolder(folderKey: string | any[], bucket: string, msalInstance: IMsalContext, region: any, agCredsResponseData: IGetAgCredsResponse | boolean, includeFolderPlaceholders = false) {
  let credentials;
  let effectiveRegion = region;

  // Priority 1: Use agCredsResponseData if available
  if (typeof agCredsResponseData === "object" && "aws_access_key_id" in agCredsResponseData) {
    credentials = {
      accessKeyId: agCredsResponseData.aws_access_key_id,
      secretAccessKey: agCredsResponseData.aws_secret_access_key,
      sessionToken: agCredsResponseData.aws_session_token,
    };
  } else {
    // Priority 2: Fallback to localStorage session
    const creds = await fetchAwsCredentials(msalInstance, bucket);
    if (!creds) throw new Error("No AWS credentials available");
    credentials = {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken,
    };
    effectiveRegion = region || creds.region;
  }

  const s3 = new S3Client({
    region: effectiveRegion,
    credentials,
  });

  const files: {
    relativePath?: any;
    key: any; name: any; // relative path
    lastModified: any; timestamp: string; class: string; size: any; isFolder: boolean; bucket: any; region: any;
  }[] = [];
  let continuationToken = undefined;
  do {
    const params: any = {
      Bucket: bucket,
      Prefix: folderKey,
      ContinuationToken: continuationToken,
    };
    const command = new ListObjectsV2Command(params);
    const data = await s3.send(command);

    if (Array.isArray(data.Contents)) {
      data.Contents.forEach((c) => {
        if (!c.Key || c.Key === folderKey) return;
        if (c.Key.endsWith("/") && !includeFolderPlaceholders) return;
        files.push({
          key: c.Key,
          name: c.Key.substring(folderKey.length),
          lastModified: c.LastModified || null,
          timestamp: c.LastModified ? new Date(c.LastModified).toISOString() : "",
          class: "Standard",
          size: c.Size,
          isFolder: false,
          bucket: bucket,
          region,
        });
      });
    }


    continuationToken = data.IsTruncated ? data.NextContinuationToken : undefined;
  } while (continuationToken);

  return files;
}

export const fetchObjectsList = async (
  msalInstance: IMsalContext,
  bucket_name: string,
  region_name: string,
  spacePrefix: string,
  agCredsResponseData?: any,
  opts?: { maxKeys?: number; continuationToken?: string },
): Promise<{ data: any; isTruncated?: boolean; nextContinuationToken?: string }> => {
  let session: Credentials | undefined = undefined;
  let credentials;
  if (agCredsResponseData && typeof agCredsResponseData === "object" && agCredsResponseData.aws_access_key_id) {
    credentials = {
      accessKeyId: agCredsResponseData.aws_access_key_id,
      secretAccessKey: agCredsResponseData.aws_secret_access_key,
      sessionToken: agCredsResponseData.aws_session_token,
    };
  } else {
    try {
      session = await loadAwsSession();
    } catch (err) {
      console.warn("loadAwsSession failed, refreshing credentials:", err);
      const raw = await generateKeysApi(msalInstance);
      session = raw.data.session;
      if (session) {
        await storeAwsSession(session);
      }
    }
    credentials = {
      accessKeyId: session && session.AccessKeyId ? session.AccessKeyId : "",
      secretAccessKey: session && session.SecretAccessKey ? session.SecretAccessKey : "",
    };
  }

  const client = new S3Client({
    logger: console,
    region: region_name,
    credentials,
    systemClockOffset: 0,
  });

  try {
    // 👇 only ONE call now
    const page: any = await client.send(new ListObjectsV2Command({
      Bucket: bucket_name,
      Prefix: spacePrefix,
      Delimiter: "/",
      ContinuationToken: opts?.continuationToken,
      MaxKeys: opts?.maxKeys ?? 1000,   // keep your old default
    }));

    return {
      data: {
        Contents: page.Contents ?? [],
        CommonPrefixes: page.CommonPrefixes ?? [],
        Prefix: spacePrefix,
      },
      isTruncated: !!page.IsTruncated,
      nextContinuationToken: page.NextContinuationToken,
    };
  } catch (error: any) {
    console.error(
      `[fetchSpaces] bucket=${bucket_name} prefix=${spacePrefix} →`,
      error,
      "\n--- metadata:",
      error.$metadata,
      "\n--- raw HTTP body:",
      error.$response?.body,
    );
    if (error.$metadata?.httpStatusCode === 403) {
      const e403 = new Error("ACCESS_DENIED");
      (e403 as any).code = 403;
      (e403 as any).status = 403;
      (e403 as any).name = "AccessDenied";
      throw e403;
    }
    throw error;
  }
};



export const getObjectsByDomainName = async (
  msalInstance: IMsalContext,
  buckets: string[],
  region: string,
  spacePrefix: string,
  agCredsResponseData?: any,
  opts?: { maxKeys?: number; continuationToken?: string },
): Promise<{
  data: any;
  isTruncated?: boolean;
  nextContinuationToken?: string;
}> => { 
  for (const bucket of buckets) {
    return fetchObjectsList(
      msalInstance,
      bucket,
      region,
      spacePrefix,
      agCredsResponseData,
      opts, // 👈 pass it
    );
  }
  return { data: [] };
};



export const getAgCreds = async (msalInstance: IMsalContext, payload: IGetAgCreds): Promise<IGetAgCredsResponse | boolean> => {
  try {
    const token = await getS3grantAccessToken(msalInstance, true);
    const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
    const requestURL = `${NEXUS_API_BASE_URL}/nexus/get-ag-creds`;

    const config = {
      headers: {
        Authorization: bearerToken,
        "Content-Type": "application/json",
      },
    };
    payload.at = token?.at;
    const response = await axios.post(requestURL, payload, config);
    if (Object.keys(response.data).length === 0) return false;
    return {
      aws_access_key_id: response.data.aws_access_key_id,
      aws_secret_access_key: response.data.aws_secret_access_key,
      aws_session_token: response.data.aws_session_token,
      access_level: response.data.access_level || "READ",
      user_role: response.data.user_role || "ro",
    }; // Adjust based on the expected response structure
  } catch (error: any) {
    console.log("Error response:", error);

    // Check if it's an axios error with response data
    if (error.response) {
      // Create a more structured error that preserves all necessary information
      const enhancedError: any = new Error(error.response.data?.detail || error.message || "API request failed");
      enhancedError.status = error.response.status;
      enhancedError.detail = error.response.data?.detail;
      enhancedError.response = error.response;
      enhancedError.isAxiosError = true;
      console.log("Throwing enhanced error:", enhancedError);
      throw enhancedError;
    }

    // For other types of errors, just re-throw
    throw error;
  }
};


export const getObjectsWithVersions = async (
  msalInstance: IMsalContext,
  bucket: string,
  region: string,
  prefix: string,
  agCredsResponseData: IGetAgCredsResponse | boolean,
  _opts?: { maxKeys?: number; continuationToken?: string }, // we ignore these on purpose, we want *all*
): Promise<{
  data: any;
  isTruncated?: boolean;
  nextContinuationToken?: string;
}> => {
  // 1) make client (same logic you already had)
  let credentials;
  let finalRegion = region;

  if (typeof agCredsResponseData === "object" && "aws_access_key_id" in agCredsResponseData) {
    credentials = {
      accessKeyId: agCredsResponseData.aws_access_key_id,
      secretAccessKey: agCredsResponseData.aws_secret_access_key,
      sessionToken: agCredsResponseData.aws_session_token,
    };
  } else {
    const fallback = await fetchAwsCredentials(msalInstance, bucket);
    if (!fallback) throw new Error("No AWS credentials available");
    credentials = {
      accessKeyId: fallback.accessKeyId,
      secretAccessKey: fallback.secretAccessKey,
      sessionToken: fallback.sessionToken,
    };
    finalRegion = finalRegion || fallback.region;
  }

  const s3 = new S3Client({ region: finalRegion, credentials });

  const allObjects: Array<any> = [];

  const folderMap: Record<string, { deleted: boolean; inCurrentListing: boolean }> = {};


  let ct: string | undefined = undefined;
  do {
    const page:any = await s3.send(new ListObjectsV2Command({
      Bucket: bucket,
      Prefix: prefix,
      Delimiter: "/",      // <- only current folder, not recursive
      MaxKeys: 1000,
      ContinuationToken: ct,
    }));

    (page.Contents ?? []).forEach((obj:any) => {

      // Only the folder placeholder is skipped here; a real file can have a
      // key equal to the prefix when searching by a complete file name.
      if (!obj.Key || (obj.Key === prefix && obj.Key.endsWith("/"))) return;

      allObjects.push(obj);
    });

    // live subfolders
    (page.CommonPrefixes ?? []).forEach((cp: CommonPrefix) => {
      if (!cp.Prefix) return;
      if (!folderMap[cp.Prefix]) {
        folderMap[cp.Prefix] = { deleted: false, inCurrentListing: true };
      } else {
        folderMap[cp.Prefix].inCurrentListing = true;
      }
    });

    ct = page.IsTruncated ? page.NextContinuationToken : undefined;
  } while (ct);

  let keyMarker: string | undefined = undefined;
  let versionIdMarker: string | undefined = undefined;

  do {
    const vpage:any = await s3.send(new ListObjectVersionsCommand({
      Bucket: bucket,
      Prefix: prefix,
      Delimiter: "/", 
      MaxKeys: 1000,
      KeyMarker: keyMarker,
      VersionIdMarker: versionIdMarker,
    }));

    // 3a) any folders that exist only in versions
    (vpage.CommonPrefixes ?? []).forEach((cp: CommonPrefix) => {
      if (!cp.Prefix) return;
      if (!folderMap[cp.Prefix]) {
        // Folder only appears in version listing, not in current listing = deleted
        folderMap[cp.Prefix] = { deleted: true, inCurrentListing: false };
      }
      // If already exists, don't change - it was set in the current listing
    });

    // 3b) handle delete markers
    (vpage.DeleteMarkers ?? []).forEach((dm: DeleteMarkerEntry) => {
      if (!dm.Key) return;
      if (dm.Key === prefix && dm.Key.endsWith("/")) return;

      const isFolder = dm.Key.endsWith("/");

      if (isFolder) {
        // deleted folder → must show as folder with (deleted)
        if (!folderMap[dm.Key]) {
          folderMap[dm.Key] = { deleted: true, inCurrentListing: false };
        } else {
          folderMap[dm.Key].deleted = true;
        }
      } else {
        // deleted FILE → add to Contents if not already present
        const already = allObjects.find((o) => o.Key === dm.Key);
        if (!already) {
          allObjects.push({
            Key: dm.Key,
            LastModified: dm.LastModified,
            Size: 0,
            StorageClass: "DELETED",
            __isDeleted: true,
          });
        }
      }
    });

    keyMarker = vpage.NextKeyMarker;
    versionIdMarker = vpage.NextVersionIdMarker;
  } while (keyMarker || versionIdMarker);


  const commonPrefixes = Object.keys(folderMap).map((pfx) => ({
    Prefix: pfx,
    __isDeleted: folderMap[pfx].deleted,
  }));

 
  return {
    data: {
      Contents: allObjects,
      CommonPrefixes: commonPrefixes,
      Prefix: prefix,
    },
    isTruncated: false,
    nextContinuationToken: undefined,
  };
};


export async function getVersionsSummaryForPrefix(
  msalInstance: IMsalContext,
  bucket: string,
  region: string,
  prefix: string,
  agCredsResponseData: IGetAgCredsResponse | boolean,
): Promise<Record<string, { count: number; hasPrevious: boolean }>> {

  let credentials;
  let finalRegion = region;

  // Priority 1: Use agCredsResponseData if available
  if (typeof agCredsResponseData === "object" && "aws_access_key_id" in agCredsResponseData) {
    credentials = {
      accessKeyId: agCredsResponseData.aws_access_key_id,
      secretAccessKey: agCredsResponseData.aws_secret_access_key,
      sessionToken: agCredsResponseData.aws_session_token,
    };
  } else {
    // Priority 2: Fallback to localStorage session
    const fallback = await fetchAwsCredentials(msalInstance, bucket);
    if (!fallback) throw new Error("No AWS credentials available");

    credentials = {
      accessKeyId: fallback.accessKeyId,
      secretAccessKey: fallback.secretAccessKey,
      sessionToken: fallback.sessionToken,
    };
    finalRegion = finalRegion || fallback.region;
  }

  const s3 = new S3Client({ region: finalRegion, credentials });

  const summary: Record<string, { count: number; hasPrevious: boolean }> = {};
  let KeyMarker: string | undefined;
  let VersionIdMarker: string | undefined;

  do {
    const resp = await s3.send(new ListObjectVersionsCommand({
      Bucket: bucket,
      Prefix: prefix,
      Delimiter: "/", // 👈 ADD THIS: Only get objects at current folder level, not subfolders
      KeyMarker,
      VersionIdMarker,
      MaxKeys: 1000,
    }));

    // Process each key separately to count only previous versions (excluding current and delete markers)
    const keyVersions: Record<string, any[]> = {};

    // Group versions by key
    if (resp.Versions) {
      resp.Versions.forEach(v => {
        const k = v?.Key as any;
        if (!k || k.endsWith("/")) return; // ignore folder placeholders
        // 👈 ADDITIONAL CHECK: Only process files directly under the current prefix
        const relativePath = k.substring(prefix.length);
        if (relativePath.includes("/")) return; // Skip files in subfolders

        if (!keyVersions[k]) keyVersions[k] = [];
        keyVersions[k].push({ ...v, __type: "Version" });
      });
    }

    // Process each key's versions
    Object.keys(keyVersions).forEach(k => {
      const versions = keyVersions[k];

      // Sort by LastModified descending to find current version
      versions.sort((a, b) => {
        const at = a.LastModified ? new Date(a.LastModified).getTime() : 0;
        const bt = b.LastModified ? new Date(b.LastModified).getTime() : 0;
        return bt - at;
      });

      // Count only non-current versions (exclude the first/latest version)
      const previousVersionsCount = Math.max(0, versions.length - 1);

      summary[k] = {
        count: previousVersionsCount,
        hasPrevious: previousVersionsCount > 0,
      };
    });

    KeyMarker = resp.NextKeyMarker;
    VersionIdMarker = resp.NextVersionIdMarker;
    if (!resp.IsTruncated) break;
  } while (true);

  return summary;
}

/**
 * Get objects including deleted ones (for versions view)
 */
export async function getObjectVersions(
  bucket: string,
  key: string,
  msalInstance: IMsalContext,
  region: string,
  agCredsResponseData: IGetAgCredsResponse | boolean,
) {
  try {
    let credentials;
    let finalRegion = region;

    if (typeof agCredsResponseData === "object" && "aws_access_key_id" in agCredsResponseData) {
      credentials = {
        accessKeyId: agCredsResponseData.aws_access_key_id,
        secretAccessKey: agCredsResponseData.aws_secret_access_key,
        sessionToken: agCredsResponseData.aws_session_token,
      };
    } else {
      const creds = await fetchAwsCredentials(msalInstance, bucket);
      if (!creds) throw new Error("No AWS credentials available");
      credentials = {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
      };
      finalRegion = finalRegion || creds.region;
    }

    const s3 = new S3Client({ region: finalRegion, credentials });

    const versions: NonNullable<ListObjectVersionsCommandOutput["Versions"]> = [];
    let KeyMarker: string | undefined;
    let VersionIdMarker: string | undefined;

    do {
      const resp = await s3.send(new ListObjectVersionsCommand({
        Bucket: bucket,
        Prefix: key,
        KeyMarker,
        VersionIdMarker,
        MaxKeys: 1000,
      }));

      if (resp.Versions) versions.push(...resp.Versions);

      KeyMarker = resp.NextKeyMarker;
      VersionIdMarker = resp.NextVersionIdMarker;
    } while (KeyMarker || VersionIdMarker);

    return versions;
  } catch (err) {
    console.error("Failed to fetch versions:", err);
    return [];
  }
}
